/**
 * Stream B — B76: In-App NPS/CSAT Collection
 *
 * NPS: quarterly "How likely to recommend?" (0-10)
 * CSAT: post-interaction "How was your experience?" (1-5 stars)
 * Real apiFetch. No mocks. CSS variables only.
 */
import { useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { X, Send, MessageSquare } from "lucide-react";

// --- NPS Modal ---

interface NpsModalProps {
  isOpen: boolean;
  onClose: () => void;
  context?: string;
}

export function NpsModal({ isOpen, onClose, context }: NpsModalProps) {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (score === null) return;
    setIsSubmitting(true);
    try {
      await apiFetch("/v1/platform/feedback/nps", {
        method: "POST",
        body: JSON.stringify({ score, comment: comment.trim() || null, context }),
      });
      setSubmitted(true);
    } catch {
      // Silently fail — don't block user workflow for feedback
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const scoreColor = (s: number) =>
    s <= 6 ? "text-[var(--color-error)]" : s <= 8 ? "text-[var(--color-warning)]" : "text-[var(--color-success)]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="relative w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute right-4 top-4 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]">
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="text-center py-4">
            <MessageSquare className="mx-auto h-10 w-10 text-[var(--color-success)] mb-3" />
            <p className="font-medium text-[var(--color-foreground)]">Thank you for your feedback!</p>
            <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Your input helps us improve.</p>
            <button type="button" onClick={onClose} className="mt-4 text-sm text-[var(--color-primary)] hover:underline">Close</button>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-1">How likely are you to recommend Ease?</h2>
            <p className="text-sm text-[var(--color-muted-foreground)] mb-4">0 = Not at all, 10 = Extremely likely</p>

            <div className="flex justify-between mb-4">
              {Array.from({ length: 11 }, (_, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setScore(i)}
                  className={`flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                    score === i
                      ? `bg-[var(--color-primary)] text-white`
                      : "bg-[var(--color-surface)] text-[var(--color-foreground)] hover:bg-[var(--color-primary)]/10"
                  }`}
                >
                  {i}
                </button>
              ))}
            </div>

            {score !== null && (
              <p className={`text-center text-sm font-medium mb-3 ${scoreColor(score)}`}>
                {score <= 6 ? "We'll do better" : score <= 8 ? "Thanks for the feedback" : "Glad you love it!"}
              </p>
            )}

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Any additional feedback? (optional)"
              rows={2}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] resize-none mb-4"
            />

            <button
              type="button"
              onClick={handleSubmit}
              disabled={score === null || isSubmitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? "Submitting..." : <><Send className="h-4 w-4" /> Submit</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

